/**
 * confirmation-summary-logic — pure presentation helpers for the booking
 * confirmation screen (Phase 10.B visual restyle).
 *
 * The design handover (flow-confirmation.html) renders the People summary
 * row as "{N} attendees · X on-site, Y remote". The split is derived from
 * the per-attendee `presence` field BookMeetingService already carries
 * ('onsite' | 'remote', set by the attendee chip). When no attendee has an
 * explicit presence value (e.g. legacy drafts hydrated before the chip
 * existed), `hasPresenceData` is false and the caller falls back to the
 * plain "{N} people" label rather than implying a split we don't know.
 *
 * Pure + side-effect free — gated at 100% coverage in
 * vitest.coverage.config.ts.
 */

export interface PresenceSplit {
  /** Attendees joining in a booked room (presence 'onsite' or unset). */
  onsite: number;
  /** Attendees dialling in (presence === 'remote'). */
  remote: number;
  /** True when at least one attendee carries an explicit presence value. */
  hasPresenceData: boolean;
}

export function presenceSplit(
  attendees: ReadonlyArray<{ presence?: 'onsite' | 'remote' }>,
): PresenceSplit {
  let onsite = 0;
  let remote = 0;
  let hasPresenceData = false;
  for (const a of attendees) {
    if (a.presence === 'remote') {
      remote += 1;
      hasPresenceData = true;
    } else {
      onsite += 1;
      if (a.presence === 'onsite') hasPresenceData = true;
    }
  }
  return { onsite, remote, hasPresenceData };
}
